import React, { useCallback, useEffect, useRef, useState } from 'react';
import NoData from './NoData';

import { isBitmovinPlayerEnabled } from '../../utils/videoPlayerFeatureFlag';

const bitmovinKey = process.env.REACT_APP_BITMOVIN_PLAYER_KEY;
const BITMOVIN_SCRIPT_SRC = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js';
const BITMOVIN_CSS_HREF = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css';

let bitmovinLoadPromise = null;

const ensureBitmovinAssets = () => {
  if (window.bitmovin?.player) return Promise.resolve();

  if (!document.querySelector(`link[href="${BITMOVIN_CSS_HREF}"]`)) {
    const link = document.createElement('link');
    link.href = BITMOVIN_CSS_HREF;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  if (!bitmovinLoadPromise) {
    bitmovinLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${BITMOVIN_SCRIPT_SRC}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = BITMOVIN_SCRIPT_SRC;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return bitmovinLoadPromise;
};

const buildBitmovinSource = (src, title, poster, preload = true) => {
  if (!src || typeof src !== 'string') return null;

  const cleanUrl = src.split('?')[0].toLowerCase();
  const source = {
    title: title || 'Video',
    poster: poster || '',
    options: {
      adaptation: { preload },
      scaling: { fit: 'contain' }
    }
  };

  if (cleanUrl.endsWith('.m3u8')) {
    return { ...source, hls: src };
  }

  return { ...source, progressive: src };
};

function BitmovinPlayer({
  src,
  title = 'Video',
  poster = '',
  className = '',
  style,
  aspectRatio = '16:9',
  autoplay = false,
  muted = false,
  lazy = false,
  objectFit = 'cover',
  onTimeUpdate,
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const timeUpdateTimerRef = useRef(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;
  const [status, setStatus] = useState('idle');
  const [activated, setActivated] = useState(() => !lazy || !isBitmovinPlayerEnabled());
  const [useBitmovin, setUseBitmovin] = useState(() => isBitmovinPlayerEnabled());

  useEffect(() => {
    const refreshPlayerFlag = () => {
      const enabled = isBitmovinPlayerEnabled();
      setUseBitmovin(enabled);
      if (!enabled) setActivated(true);
    };
    window.addEventListener('storage', refreshPlayerFlag);
    return () => window.removeEventListener('storage', refreshPlayerFlag);
  }, []);

  const stopTimeUpdates = useCallback(() => {
    if (timeUpdateTimerRef.current) {
      window.clearInterval(timeUpdateTimerRef.current);
      timeUpdateTimerRef.current = null;
    }
  }, []);

  const emitTimeUpdate = useCallback(() => {
    if (!onTimeUpdateRef.current || !playerRef.current) return;
    try {
      onTimeUpdateRef.current({
        currentTime: playerRef.current.getCurrentTime(),
        duration: playerRef.current.getDuration()
      });
    } catch {
      // Player can throw while loading or tearing down.
    }
  }, []);

  const startTimeUpdates = useCallback(() => {
    if (!onTimeUpdateRef.current) return;
    stopTimeUpdates();
    emitTimeUpdate();
    timeUpdateTimerRef.current = window.setInterval(emitTimeUpdate, 500);
  }, [emitTimeUpdate, stopTimeUpdates]);

  useEffect(() => {
    let cancelled = false;
    if (!activated) {
      setStatus('idle');
      return undefined;
    }

    const source = buildBitmovinSource(src, title, poster, !lazy);

    const destroyPlayer = () => {
      stopTimeUpdates();
      const player = playerRef.current;
      playerRef.current = null;
      if (!player) return;
      try {
        const result = player.destroy();
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      } catch {
        // Ignore cleanup errors from partially initialized instances.
      }
    };

    if (!useBitmovin) {
      destroyPlayer();
      setStatus(src ? 'ready' : 'empty');
      return destroyPlayer;
    }

    if (!source || !containerRef.current) {
      setStatus('empty');
      destroyPlayer();
      return destroyPlayer;
    }

    setStatus('loading');

    ensureBitmovinAssets()
      .then(() => {
        if (cancelled || !containerRef.current || !window.bitmovin?.player) return null;

        destroyPlayer();

        const player = new window.bitmovin.player.Player(containerRef.current, {
          key: bitmovinKey,
          playback: { autoplay, muted },
          analytics: false,
          style: {
            width: '100%',
            aspectratio: aspectRatio
          }
        });

        playerRef.current = player;
        player.on('play', startTimeUpdates);
        player.on('seeked', emitTimeUpdate);
        player.on('paused', () => {
          emitTimeUpdate();
          stopTimeUpdates();
        });
        player.on('playbackfinished', () => {
          emitTimeUpdate();
          stopTimeUpdates();
        });
        player.on('error', () => {
          setStatus('error');
          stopTimeUpdates();
        });

        return player.load(source);
      })
      .then(() => {
        if (cancelled) return;
        setStatus('ready');
        const watermark = containerRef.current?.querySelector('.bmpui-ui-watermark');
        if (watermark) watermark.style.display = 'none';
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      destroyPlayer();
    };
  }, [activated, aspectRatio, autoplay, emitTimeUpdate, lazy, muted, poster, src, startTimeUpdates, stopTimeUpdates, title, useBitmovin]);

  const showBitmovinError = useBitmovin && status === 'error';
  const containFit = objectFit === 'contain';
  const boxed = containFit && style?.height != null && style.height !== 'auto';
  const playerClassName = `tw-relative tw-w-full tw-overflow-hidden tw-rounded-[inherit] tw-bg-[#000] common-bitmovin-player ${containFit ? "tw-flex tw-items-center tw-justify-center tw-bg-[#000] [&_.common-bitmovin-player\\_\\_poster]:tw-w-full [&_.common-bitmovin-player\\_\\_poster]:tw-h-auto [&_.common-bitmovin-player\\_\\_poster]:tw-max-h-full [&_.common-bitmovin-player\\_\\_poster]:tw-min-h-[0] [&_.common-bitmovin-player\\_\\_poster]:tw-object-contain [&_.common-bitmovin-player\\_\\_poster]:tw-bg-[#000] [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-w-full [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-h-auto [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-max-h-full [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-min-h-[0] [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-object-contain [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-bg-[#000] [&_video]:tw-w-full [&_video]:tw-h-auto [&_video]:tw-max-h-full [&_video]:tw-min-h-[0] [&_video]:tw-object-contain [&_video]:tw-bg-[#000] [&_.common-bitmovin-player\\_\\_surface]:!tw-w-full [&_.common-bitmovin-player\\_\\_surface]:!tw-h-full [&_.bitmovinplayer-container]:!tw-w-full [&_.bitmovinplayer-container]:!tw-h-full [&_.bmpui-ui-uicontainer]:!tw-w-full [&_.bmpui-ui-uicontainer]:!tw-h-full common-bitmovin-player--contain" : ""} ${boxed ? "tw-w-full tw-h-full [&_.common-bitmovin-player\\_\\_poster]:tw-w-full [&_.common-bitmovin-player\\_\\_poster]:tw-h-full [&_.common-bitmovin-player\\_\\_poster]:tw-max-h-[none] [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-w-full [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-h-full [&_.common-bitmovin-player\\_\\_poster-fallback]:tw-max-h-[none] [&_video]:tw-w-full [&_video]:tw-h-full [&_video]:tw-max-h-[none] common-bitmovin-player--boxed" : ""} ${className}`.trim();
  const playerStyle = boxed
    ? { width: style.width || '100%', ...style }
    : style;
  const videoFillStyle = containFit
    ? boxed
      ? { width: '100%', height: '100%', maxHeight: 'none', objectFit: 'contain', borderRadius: 8, background: '#000' }
      : { width: '100%', maxHeight: style?.maxHeight || '60vh', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: 8, background: '#000' }
    : { width: '100%', maxHeight: '60vh', borderRadius: 8, background: '#000' };

  if (!activated) {
    return (
      <button
        type="button"
        className={`${playerClassName}   tw-block tw-border-0 tw-p-0 tw-min-h-[180px] tw-cursor-pointer common-bitmovin-player--lazy`.trim()}
        style={playerStyle}
        onClick={() => setActivated(true)}
        aria-label={`Play ${title}`}
      >
        {poster ? (
          <img src={poster} alt="" className="tw-w-full tw-min-h-[180px] tw-object-cover tw-bg-[#111] common-bitmovin-player__poster" />
        ) : src ? (
          <video
            src={src}
            muted
            playsInline
            preload="metadata"
            className="tw-w-full tw-min-h-[180px] tw-object-cover tw-bg-[#111] common-bitmovin-player__poster"
          />
        ) : (
          <div className="tw-w-full tw-min-h-[180px] tw-object-cover tw-bg-[#111] common-bitmovin-player__poster-fallback" />
        )}
        <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-[#fff] tw-font-semibold [background:rgba(0,_0,_0,_0.35)] common-bitmovin-player__play">Play</span>
      </button>
    );
  }

  return (
    <div className={playerClassName} style={playerStyle}>
      {useBitmovin ? (
        <div ref={containerRef} className="tw-w-full common-bitmovin-player__surface" />
      ) : (
        <video
          controls
          autoPlay={autoplay}
          muted={muted}
          preload="metadata"
          src={src}
          onTimeUpdate={(event) => onTimeUpdate?.({
            currentTime: event.currentTarget.currentTime,
            duration: event.currentTarget.duration,
          })}
          onSeeked={(event) => onTimeUpdate?.({
            currentTime: event.currentTarget.currentTime,
            duration: event.currentTarget.duration,
          })}
          style={videoFillStyle}
        />
      )}
      {useBitmovin && status === 'loading' && <div className="tw-absolute tw-inset-0 tw-z-[2] tw-flex tw-items-center tw-justify-center tw-p-4 tw-text-[#fff] tw-text-[0.9rem] tw-text-center tw-pointer-events-none [background:rgba(0,_0,_0,_0.72)] common-bitmovin-player__state">Loading video...</div>}
      {useBitmovin && status === 'empty' && (
        <div className="tw-absolute tw-inset-0 tw-z-[2] tw-flex tw-items-center tw-justify-center tw-p-4 tw-text-[#fff] tw-text-[0.9rem] tw-text-center tw-pointer-events-none [background:rgba(0,_0,_0,_0.72)] common-bitmovin-player__state">
          <NoData message="No video available" icon="fluent:video-24-regular" compact />
        </div>
      )}
      {showBitmovinError && (
        <div className="tw-absolute tw-inset-0 tw-z-[2] tw-flex tw-items-center tw-justify-center tw-p-4 tw-text-[0.9rem] tw-text-center tw-pointer-events-none [background:rgba(0,_0,_0,_0.72)] tw-text-[#fecaca] common-bitmovin-player__state common-bitmovin-player__state--error">
          {src ? (
            <div style={{ width: '100%', height: '100%' }}>
              <video
                controls
                preload="metadata"
                style={videoFillStyle}
                src={src}
                onTimeUpdate={(event) => onTimeUpdate?.({
                  currentTime: event.currentTarget.currentTime,
                  duration: event.currentTarget.duration,
                })}
                onSeeked={(event) => onTimeUpdate?.({
                  currentTime: event.currentTarget.currentTime,
                  duration: event.currentTarget.duration,
                })}
              />
            </div>
          ) : (
            'Unable to load this video.'
          )}
        </div>
      )}
    </div>
  );
}

export default BitmovinPlayer;
